import { useEffect, useState } from "react";
import { CrmLayout } from "@/components/crm/CrmLayout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Users, MailCheck, Phone, FileText, Trophy, TrendingUp, Bell, Loader2 } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tint }) => (
  <Card className="p-5">
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-500">{label}</span>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}><Icon size={18} /></div>
    </div>
    <p className="font-display text-3xl font-bold text-slate-900 mt-2" data-testid={`stat-${label}`}>{value}</p>
  </Card>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/crm/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <CrmLayout><div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-blue-600" /></div></CrmLayout>;

  const funnel = [
    { name: "Leads", v: d.total_leads }, { name: "Vérifiés", v: d.verified_emails },
    { name: "Qualifiés", v: d.qualified }, { name: "Devis", v: d.devis_demande + d.devis_transmis }, { name: "Gagnés", v: d.won },
  ];
  const split = [{ name: "Mutuelle France", value: d.split.mutuelle_france }, { name: "Expatrié", value: d.split.expatrie }];
  const COLORS = ["#2563EB", "#0B192C"];

  return (
    <CrmLayout>
      <h1 className="font-display text-2xl font-bold text-slate-900">Tableau de bord</h1>
      <p className="text-slate-500 text-sm mt-1">Vue d'ensemble de votre activité commerciale.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="crm-dashboard-stats">
        <Stat icon={Users} label="Nouveaux leads" value={d.new_leads} tint="bg-slate-100 text-slate-600" />
        <Stat icon={MailCheck} label="E-mails vérifiés" value={d.verified_emails} tint="bg-sky-100 text-sky-600" />
        <Stat icon={Phone} label="À rappeler" value={d.to_call} tint="bg-violet-100 text-violet-600" />
        <Stat icon={FileText} label="Devis demandés" value={d.devis_demande} tint="bg-amber-100 text-amber-600" />
        <Stat icon={FileText} label="Devis transmis" value={d.devis_transmis} tint="bg-orange-100 text-orange-600" />
        <Stat icon={Trophy} label="Contrats gagnés" value={d.won} tint="bg-emerald-100 text-emerald-600" />
        <Stat icon={Users} label="Total prospects" value={d.total_leads} tint="bg-blue-100 text-blue-600" />
        <Stat icon={TrendingUp} label="Taux conversion" value={`${d.conversion_rate}%`} tint="bg-teal-100 text-teal-600" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Entonnoir de conversion</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={funnel}><XAxis dataKey="name" tick={{ fontSize: 12 }} /><Tooltip /><Bar dataKey="v" fill="#2563EB" radius={[6, 6, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Répartition</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart><Pie data={split} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75}>{split.map((e, i) => <Cell key={i} fill={COLORS[i]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 text-sm">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-600 inline-block" />France</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-brand-navy inline-block" style={{ background: "#0B192C" }} />Expatrié</span>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <Card className="p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-4 flex items-center gap-2"><Bell size={18} /> Notifications</h3>
          {d.notifications.length === 0 ? <p className="text-sm text-slate-400">Aucune notification.</p> : (
            <ul className="space-y-2">{d.notifications.map((n) => (
              <li key={n.id} className="text-sm flex items-center justify-between border-b border-slate-100 pb-2">
                <span className="text-slate-700">{n.message}</span><Badge variant="secondary" className="text-xs">{n.type}</Badge>
              </li>))}
            </ul>)}
        </Card>
        <Card className="p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Sources d'acquisition</h3>
          {d.sources.map((s) => (
            <div key={s.source} className="flex justify-between text-sm py-1.5 border-b border-slate-100">
              <span className="text-slate-600">{s.source}</span><span className="font-semibold">{s.count}</span>
            </div>))}
        </Card>
      </div>
    </CrmLayout>
  );
}
